// Estado de la aplicación como reductor puro (sin React), para poder probarlo sin DOM.
import { listMounts, selectMotor, setMountMark, setMountMotor, updateLaunch, type LaunchConditions, type Motor, type Rocket, type SimulationResult, type UnitSystem } from '@openrocket-web/physics';
import { addComponent, emptyRocket, exampleRocket, moveComponent, removeComponent, updateComponent, type ComponentKind } from './editor';
import { applyConfig, configFromRocket, type MotorAssignment, type MotorConfig } from './configs';
import { DEMO_MOTOR, findMotor, mergeMotors } from './motors';
import { defaultSim, type Prefs, type SimDef, type SimSettings, type Theme, type WindSettings } from './persist';

/** Resultado de una corrida y con qué se calculó, para avisar cuando ya no coincide con el diseño. */
export interface SimRun {
  readonly result: SimulationResult;
  readonly basis: readonly unknown[];
}

export interface Simulation extends SimDef {
  readonly run?: SimRun;
}

export interface AppState {
  readonly rocket: Rocket;
  readonly selectedId: string | undefined;
  readonly sims: readonly Simulation[];
  /** Configuraciones de motor del diseño; los soportes llevan los motores de la activa. */
  readonly configs: readonly MotorConfig[];
  readonly activeConfigId: string;
  readonly units: UnitSystem;
  readonly theme: Theme;
  readonly motors: readonly Motor[];
  /** Avisos y errores no bloqueantes para el usuario (carga de archivos, selección de motor...). */
  readonly notices: readonly Notice[];
  /** El diseño tiene cambios sin exportar: al salir de la página se avisa, porque no se guarda. */
  readonly isDirty: boolean;
}

export interface Notice {
  readonly id: number;
  readonly level: 'info' | 'warning' | 'error';
  readonly text: string;
}

/** Nombre para mostrar: el nombre puede quedar vacío mientras se escribe. */
export const simLabel = (sim: Pick<SimDef, 'name'>): string => (sim.name.trim() === '' ? 'Simulación sin nombre' : sim.name);

/** Qué se compara para saber si los resultados siguen al día. */
type WithConfigs = Pick<AppState, 'rocket' | 'motors' | 'configs' | 'activeConfigId'>;

/** Configuración con la que se simula: la de la simulación, o la activa si no tiene o ya no existe. */
export const configOf = (s: Pick<AppState, 'configs' | 'activeConfigId'>, sim: Pick<SimDef, 'configId'>): MotorConfig =>
  s.configs.find((c) => c.id === sim.configId) ?? s.configs.find((c) => c.id === s.activeConfigId) ?? s.configs[0]!;

export const simBasis = (s: WithConfigs, sim: SimDef): readonly unknown[] => [s.rocket, s.motors, configOf(s, sim), sim.launch, sim.wind, sim.sim];
export const isStale = (s: WithConfigs, sim: Simulation): boolean =>
  sim.run !== undefined && !simBasis(s, sim).every((v, i) => v === sim.run!.basis[i]);

/** Simulación que llega de un archivo importado. */
export interface ImportedSim {
  readonly name: string;
  readonly launch: LaunchConditions;
  readonly wind: WindSettings;
  readonly roughness?: number;
  /** Id (en `motorConfigs` de `set-rocket`) de la configuración con la que se simuló. */
  readonly configId?: string;
}

export type Action =
  | { type: 'select'; id: string | undefined }
  | { type: 'add'; kind: ComponentKind }
  | { type: 'update'; id: string; patch: Record<string, unknown> }
  | { type: 'remove'; id: string }
  | { type: 'move'; id: string; delta: -1 | 1 }
  | { type: 'rename'; name: string }
  | { type: 'set-rocket'; rocket: Rocket; sims?: readonly ImportedSim[]; motorConfigs?: readonly MotorConfig[]; activeConfigId?: string }
  | { type: 'new' }
  /** El diseño se acaba de exportar a un archivo. */
  | { type: 'exported' }
  | { type: 'example' }
  | { type: 'config-add' }
  | { type: 'config-duplicate'; id: string }
  | { type: 'config-remove'; id: string }
  | { type: 'config-rename'; id: string; name: string }
  | { type: 'config-select'; id: string }
  /** Motor de un soporte en una configuración; sin `motor` lo quita. */
  | { type: 'config-set-motor'; id: string; mountId: string; motor?: MotorAssignment }
  /** Marca o desmarca un tubo como portamotor; al desmarcarlo todas las configuraciones pierden su motor. */
  | { type: 'mount-set'; id: string; on: boolean }
  | { type: 'sim-config'; id: string; configId: string | undefined }
  | { type: 'sim-add' }
  | { type: 'sim-duplicate'; id: string }
  | { type: 'sim-remove'; id: string }
  | { type: 'sim-rename'; id: string; name: string }
  | { type: 'sim-launch'; id: string; patch: Partial<LaunchConditions> }
  | { type: 'sim-wind'; id: string; patch: Partial<WindSettings> }
  | { type: 'sim-settings'; id: string; patch: Partial<SimSettings> }
  | { type: 'sim-result'; id: string; run: SimRun }
  | { type: 'units'; units: UnitSystem }
  | { type: 'theme'; theme: Theme }
  | { type: 'add-motors'; motors: readonly Motor[] }
  | { type: 'remove-motor'; id: string }
  | { type: 'assign-motor'; mountId: string; motorId: string; delay: number }
  | { type: 'clear-motor'; mountId: string }
  | { type: 'notice'; level: Notice['level']; text: string }
  | { type: 'dismiss-notice'; id: number };

let noticeCounter = 0;
const notice = (level: Notice['level'], text: string): Notice => ({ id: ++noticeCounter, level, text });
const withNotice = (s: AppState, level: Notice['level'], text: string): AppState => ({ ...s, notices: [...s.notices.slice(-4), notice(level, text)] });

const nextConfigId = (configs: readonly MotorConfig[]): string => {
  let n = configs.length + 1;
  while (configs.some((c) => c.id === `cfg-${n}`)) n++;
  return `cfg-${n}`;
};

/** Con un diseño nuevo las configuraciones anteriores ya no valen: empieza con una que refleja sus motores. */
function resetConfigs(s: AppState, rocket: Rocket): AppState {
  const sims = s.sims.map((x) => {
    const { configId: _c, ...rest } = x;
    void _c;
    return rest;
  });
  return { ...s, rocket, selectedId: undefined, sims, configs: [configFromRocket(rocket, 'cfg-1')], activeConfigId: 'cfg-1' };
}

/** Los soportes del diseño reflejan siempre la configuración activa: tras cambiarlos a mano se copia a ella. */
function syncActive(s: AppState): AppState {
  return { ...s, configs: s.configs.map((c) => (c.id === s.activeConfigId ? { ...c, motors: configFromRocket(s.rocket, c.id).motors } : c)) };
}

function activate(s: AppState, id: string): AppState {
  const cfg = s.configs.find((c) => c.id === id);
  return cfg === undefined ? s : { ...s, activeConfigId: id, rocket: applyConfig(s.rocket, cfg) };
}

/** El diseño siempre arranca vacío; solo se restauran las preferencias. */
export function initialState(saved?: Partial<Prefs>): AppState {
  const motors = saved?.motors !== undefined && saved.motors.length > 0 ? mergeMotors([DEMO_MOTOR], saved.motors) : [DEMO_MOTOR];
  const rocket = emptyRocket();
  return {
    rocket,
    selectedId: undefined,
    configs: [configFromRocket(rocket, 'cfg-1')],
    activeConfigId: 'cfg-1',
    sims: [defaultSim('sim-1', 'Simulación 1')],
    units: saved?.units ?? 'metric',
    // Oscuro por defecto: es el aspecto de referencia de OpenRocket que se quiere reproducir.
    theme: saved?.theme ?? 'dark',
    motors,
    notices: [],
    isDirty: false,
  };
}

export const nextSimId = (sims: readonly Simulation[]): string => {
  let n = sims.length + 1;
  while (sims.some((x) => x.id === `sim-${n}`)) n++;
  return `sim-${n}`;
};
const nextSimName = (sims: readonly Simulation[]): string => {
  let n = sims.length + 1;
  while (sims.some((x) => x.name === `Simulación ${n}`)) n++;
  return `Simulación ${n}`;
};
const mapSim = (s: AppState, id: string, f: (x: Simulation) => Simulation): AppState => ({ ...s, sims: s.sims.map((x) => (x.id === id ? f(x) : x)) });

/** Acciones que cambian el diseño (rocket, configuraciones o simulaciones) y por tanto dejan cambios sin exportar. */
const EDITS: ReadonlySet<Action['type']> = new Set<Action['type']>([
  'add', 'update', 'remove', 'move', 'rename', 'example', 'config-add', 'config-duplicate', 'config-remove', 'config-rename', 'config-select',
  'config-set-motor', 'mount-set', 'sim-config', 'sim-add', 'sim-duplicate', 'sim-remove', 'sim-rename', 'sim-launch', 'sim-wind', 'sim-settings',
  'assign-motor', 'clear-motor',
]);

export function reducer(s: AppState, a: Action): AppState {
  const next = reduce(s, a);
  // Cargar un diseño, vaciarlo o exportarlo lo deja sin cambios pendientes.
  if (a.type === 'set-rocket' || a.type === 'new' || a.type === 'exported') return next.isDirty ? { ...next, isDirty: false } : next;
  return EDITS.has(a.type) && next !== s && !next.isDirty ? { ...next, isDirty: true } : next;
}

function reduce(s: AppState, a: Action): AppState {
  switch (a.type) {
    case 'select':
      return { ...s, selectedId: a.id };
    case 'add': {
      const parentId = s.selectedId;
      const r = addComponent(s.rocket, a.kind, parentId);
      if (r === undefined) return withNotice(s, 'warning', 'Este componente se añade a un tubo de cuerpo o a una transición, y el cohete no tiene ninguno.');
      return { ...s, rocket: r.rocket, selectedId: r.id };
    }
    case 'update':
      return { ...s, rocket: updateComponent(s.rocket, a.id, a.patch) };
    case 'remove':
      return { ...s, rocket: removeComponent(s.rocket, a.id), selectedId: s.selectedId === a.id ? undefined : s.selectedId };
    case 'move':
      return { ...s, rocket: moveComponent(s.rocket, a.id, a.delta) };
    case 'rename':
      return { ...s, rocket: { ...s.rocket, name: a.name } };
    case 'set-rocket':
    {
      const reset = resetConfigs(s, a.rocket);
      // Un diseño importado trae sus configuraciones de motor: sustituyen a la inicial y la activa lleva sus motores.
      const base = a.motorConfigs !== undefined && a.motorConfigs.length > 0 ? activate({ ...reset, configs: a.motorConfigs }, a.motorConfigs.some((c) => c.id === a.activeConfigId) ? a.activeConfigId! : a.motorConfigs[0]!.id) : reset;
      if (a.sims === undefined || a.sims.length === 0) return base;
      // Un diseño importado trae sus propias simulaciones: sustituyen a la lista actual.
      const sims: Simulation[] = [];
      for (const i of a.sims) {
        const d = defaultSim(nextSimId(sims), i.name === '' ? nextSimName(sims) : i.name);
        sims.push({ ...d, launch: i.launch, wind: i.wind, sim: i.roughness === undefined ? d.sim : { ...d.sim, roughness: i.roughness }, ...(i.configId !== undefined && base.configs.some((c) => c.id === i.configId) ? { configId: i.configId } : {}) });
      }
      return { ...base, sims };
    }
    case 'new':
      return resetConfigs(s, emptyRocket());
    case 'example':
      return resetConfigs(s, exampleRocket(DEMO_MOTOR.id));
    case 'config-add': {
      const cfg: MotorConfig = { id: nextConfigId(s.configs), motors: {} };
      return activate({ ...s, configs: [...s.configs, cfg] }, cfg.id);
    }
    case 'config-duplicate': {
      const src = s.configs.find((c) => c.id === a.id);
      if (src === undefined) return s;
      const cfg: MotorConfig = { id: nextConfigId(s.configs), ...(src.name === undefined ? {} : { name: `${src.name} (copia)` }), motors: src.motors };
      const at = s.configs.indexOf(src) + 1;
      return activate({ ...s, configs: [...s.configs.slice(0, at), cfg, ...s.configs.slice(at)] }, cfg.id);
    }
    case 'config-remove': {
      if (s.configs.length <= 1) return withNotice(s, 'warning', 'Debe quedar al menos una configuración de motor.');
      const configs = s.configs.filter((c) => c.id !== a.id);
      const sims = s.sims.map((x) => {
        if (x.configId !== a.id) return x;
        const { configId: _c, ...rest } = x;
        void _c;
        return rest;
      });
      const next = { ...s, configs, sims };
      return s.activeConfigId === a.id ? activate(next, configs[0]!.id) : next;
    }
    case 'config-rename': {
      const name = a.name.trim();
      return {
        ...s,
        configs: s.configs.map((c) => {
          if (c.id !== a.id) return c;
          const { name: _n, ...rest } = c;
          void _n;
          return name === '' ? rest : { ...rest, name };
        }),
      };
    }
    case 'config-select':
      return activate(s, a.id);
    case 'config-set-motor': {
      const cfg = s.configs.find((c) => c.id === a.id);
      if (cfg === undefined) return s;
      let motors: Record<string, MotorAssignment> = { ...cfg.motors };
      let warnings: readonly string[] = [];
      if (a.motor === undefined) delete motors[a.mountId];
      else {
        const motor = findMotor(s.motors, a.motor.motorId);
        if (motor === undefined) return withNotice(s, 'error', 'El motor elegido no está en la biblioteca.');
        // Se valida sobre el diseño tal como quedaría con esa configuración.
        const r = selectMotor(applyConfig(s.rocket, cfg), a.mountId, motor, a.motor.delay);
        if (!r.ok) return withNotice(s, 'error', r.error);
        motors = { ...motors, [a.mountId]: { motorId: motor.id, delay: a.motor.delay } };
        warnings = r.warnings;
      }
      const updated: MotorConfig = { ...cfg, motors };
      let next: AppState = { ...s, configs: s.configs.map((c) => (c.id === cfg.id ? updated : c)) };
      if (cfg.id === s.activeConfigId) next = { ...next, rocket: applyConfig(s.rocket, updated) };
      return warnings.length > 0 ? withNotice(next, 'warning', warnings.join(' ')) : next;
    }
    case 'mount-set': {
      const rocket = setMountMark(s.rocket, a.id, a.on ? {} : undefined);
      if (rocket === s.rocket) return s;
      if (a.on) return syncActive({ ...s, rocket });
      const configs = s.configs.map((c) => {
        if (c.motors[a.id] === undefined) return c;
        const { [a.id]: _gone, ...motors } = c.motors;
        void _gone;
        return { ...c, motors };
      });
      return { ...s, rocket, configs };
    }
    case 'sim-config':
      return mapSim(s, a.id, (x) => {
        const { configId: _c, ...rest } = x;
        void _c;
        return a.configId === undefined ? rest : { ...rest, configId: a.configId };
      });
    case 'sim-add': {
      const d = defaultSim(nextSimId(s.sims), nextSimName(s.sims));
      return { ...s, sims: [...s.sims, d] };
    }
    case 'sim-duplicate': {
      const src = s.sims.find((x) => x.id === a.id);
      if (src === undefined) return s;
      const copy: Simulation = { id: nextSimId(s.sims), name: `${src.name} (copia)`, launch: src.launch, wind: src.wind, sim: src.sim };
      const at = s.sims.indexOf(src) + 1;
      return { ...s, sims: [...s.sims.slice(0, at), copy, ...s.sims.slice(at)] };
    }
    case 'sim-remove':
      return { ...s, sims: s.sims.filter((x) => x.id !== a.id) };
    case 'sim-rename':
      return mapSim(s, a.id, (x) => ({ ...x, name: a.name }));
    case 'sim-launch': {
      const cur = s.sims.find((x) => x.id === a.id);
      if (cur === undefined) return s;
      const r = updateLaunch(cur.launch, a.patch);
      const first = Object.values(r.errors)[0];
      const next = mapSim(s, a.id, (x) => ({ ...x, launch: r.conditions }));
      return first === undefined ? next : withNotice(next, 'warning', first);
    }
    case 'sim-wind':
      return mapSim(s, a.id, (x) => ({ ...x, wind: { ...x.wind, ...a.patch } }));
    case 'sim-settings':
      return mapSim(s, a.id, (x) => ({ ...x, sim: { ...x.sim, ...a.patch } }));
    case 'sim-result':
      return mapSim(s, a.id, (x) => ({ ...x, run: a.run }));
    case 'units':
      return { ...s, units: a.units };
    case 'theme':
      return { ...s, theme: a.theme };
    case 'add-motors':
      return { ...s, motors: mergeMotors(s.motors, a.motors) };
    case 'remove-motor': {
      const motor = s.motors.find((m) => m.id === a.id);
      if (motor === undefined) return s;
      const used = listMounts(s.rocket).some((m) => m.mark.motor !== undefined && findMotor(s.motors, m.mark.motor.motorId)?.id === a.id);
      if (used) return withNotice(s, 'warning', `«${motor.designation}» está asignado a un soporte del diseño; quítalo del soporte antes de borrarlo de la biblioteca.`);
      return { ...s, motors: s.motors.filter((m) => m.id !== a.id) };
    }
    case 'assign-motor': {
      const motor = s.motors.find((m) => m.id === a.motorId);
      if (motor === undefined) return withNotice(s, 'error', 'El motor elegido no está en la biblioteca.');
      const r = selectMotor(s.rocket, a.mountId, motor, a.delay);
      if (!r.ok) return withNotice(s, 'error', r.error);
      const next = syncActive({ ...s, rocket: r.rocket });
      return r.warnings.length > 0 ? withNotice(next, 'warning', r.warnings.join(' ')) : next;
    }
    case 'clear-motor':
      return syncActive({ ...s, rocket: setMountMotor(s.rocket, a.mountId, undefined) });
    case 'exported':
      return s;
    case 'notice':
      return withNotice(s, a.level, a.text);
    case 'dismiss-notice':
      return { ...s, notices: s.notices.filter((n) => n.id !== a.id) };
  }
}

export { findMotor };
